/*  ========================================================================
    # Sections - Posts
    ========================================================================  */

import React, { useEffect, useState } from 'react';
import type { PostsProps } from '.';
import defaultTheme from 'tailwindcss/defaultTheme';

// Hooks
import useURLParams from '@/react/hooks/useURLParams';
import { usePostsApi } from '@/react/hooks/usePostsApi';
import useSVGSprite from '@/react/hooks/useSVGSprite';

// Components
import { PostCard, PostCardSkeleton } from './PostCard';
import Pagination from './Pagination';

const Posts = ({ headline, layout, selectedCategory, selectedPosts, allCategories }: PostsProps) => {
  const { params: urlParams, setUrlParams } = useURLParams();
  const [postsPerPage, setPostsPerPage] = useState(() => {
    if (window.innerWidth >= parseInt(defaultTheme.screens.lg, 10)) {
      return 6;
    } else if (window.innerWidth >= parseInt(defaultTheme.screens.sm, 10)) {
      return 4;
    } else {
      return 2;
    }
  });
  const currentPageParam = parseInt(urlParams.get('currentPage') || '1', 10);
  let categoriesParam: string[] | number[] = urlParams.get('categories')?.split(',') || [];
  categoriesParam = [...categoriesParam.map(Number)];
  const setCategoriesParam = (categories: number[]) => setUrlParams({ categories: categories.join(',') });
  const [includedCategories, setIncludedCategories] = useState(
    layout === 'category' && selectedCategory ? [selectedCategory] : categoriesParam
  );
  const [includedPosts, setIncludedPosts] = useState( // eslint-disable-line @typescript-eslint/no-unused-vars
    layout === 'selected' && selectedPosts ? selectedPosts : []
  );
  const searchValue = urlParams.get('search') || '';
  const [searchInput, setSearchInput] = useState(searchValue);
  const handlePagination = (page: number) => setUrlParams({ currentPage: page });
  const svgSprite = useSVGSprite();

  /**
   * Use WordPress Products REST API.
   */
  const { loadingPosts, postsCount, posts } = usePostsApi({
    per_page: postsPerPage,
    page: currentPageParam,
    categories: includedCategories,
    include: includedPosts,
    search: searchValue
  });

  /**
   * Handle selecting category.
   */
  const selectCategory = (categoryID: number) => {
    if (includedCategories.includes(categoryID)) {
      const tempCategories = includedCategories;
      tempCategories.splice(tempCategories.indexOf(categoryID), 1);
      if (tempCategories.length > 0) {
        setIncludedCategories([...tempCategories]);
        setCategoriesParam([...tempCategories]);
      }
      if (tempCategories.length === 0) {
        setIncludedCategories([]);
        setCategoriesParam([]);
      }
    } else {
      setIncludedCategories(prev => [...prev, categoryID]);
      setCategoriesParam([...includedCategories, categoryID]);
    }
  };

  /**
   * Check how many 'postsPerPage' are needed for the current breakpoint
   * and set current page to 1 on window resize.
   */
  useEffect(() => {
    window.addEventListener(
      'resize',
      () => {
        if (window.innerWidth >= parseInt(defaultTheme.screens.lg, 10)) {
          setPostsPerPage(6);
        } else if (window.innerWidth >= parseInt(defaultTheme.screens.sm, 10)) {
          setPostsPerPage(4);
        } else {
          setPostsPerPage(2);
        }

        setUrlParams({ currentPage: 1 });
      },
      { passive: true }
    );
  }, []);

  return (
    <div className='container'>
      {headline !== '' ? (
        <div className='w-full xl:w-10/12 xxl:w-8/12 mx-auto'>
          <div className='prose mb-14 text-center' dangerouslySetInnerHTML={{ __html: headline }} />
        </div>
      ) : null}

      {/* Filter */}
      {layout === 'all' ? (
        <div className='s-posts__filter'>
          <div className='s-posts__filter-categories'>
            <button
              className={includedCategories.length === 0 ? 'active' : ''}
              onClick={() => {
                setIncludedCategories([]);
                setCategoriesParam([]);
              }}
            >
              View All
            </button>
            {allCategories?.map(category => (
              <button
                key={category.cat_ID}
                className={`${includedCategories.includes(category.cat_ID) ? 'active' : ''}`}
                onClick={() => selectCategory(category.cat_ID)}
              >
                {category.name}
              </button>
            ))}
          </div>
          <form
            className='s-posts__filter-search'
            onSubmit={event => {
              event.preventDefault();
              setUrlParams({ currentPage: 1, search: searchInput });
            }}
          >
            <label htmlFor='section-posts-search-input'>
              <button type='submit'>
                <svg>
                  <use href={`${svgSprite}#search`} />
                </svg>
              </button>
              <input
                id='section-posts-search-input'
                type='search'
                placeholder='Search...'
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
              />
              <button
                className={`${searchInput !== '' ? 'active' : ''}`}
                onClick={() => {
                  setUrlParams({ currentPage: null, search: null });
                  setSearchInput('');
                }}
              >
                <span />
                <span />
              </button>
            </label>
          </form>
        </div>
      ) : null}

      {/* Posts */}
      <div className='row gap-y-8'>
        {loadingPosts ? (
          <>
            {[...Array(postsPerPage)].map((post, index) => (
              <div key={`${index}-skeleton`} className='col w-full sm:w-6/12 lg:w-4/12'>
                <PostCardSkeleton key={`${index}-skeleton`} />
              </div>
            ))}
          </>
        ) : null}
        {!loadingPosts && posts.length > 0 ? (
          <>
            {posts?.map((post, index) => (
              <div key={`${index}-${post.title}`} className='col w-full sm:w-6/12 lg:w-4/12'>
                <PostCard
                  permalink={post.permalink}
                  image={post.image}
                  category={post.category}
                  title={post.title}
                  excerpt={post.excerpt}
                />
              </div>
            ))}
          </>
        ) : null}
      </div>

      {/* Pagination */}
      <Pagination
        entriesCount={postsCount}
        entriesPerPage={postsPerPage}
        currentPage={currentPageParam}
        handlePagination={handlePagination}
      />
    </div>
  );
};

export default Posts;
